import { Theme, StakeholderProfile } from '../types';

export const HEALTHCARE_COLORS = {
  offWhite: '#FAFAF7',
  softBlack: '#111111',
  deepSlate: '#2C3E50',
  mutedTeal: '#5D8A8A',
  forestGreen: '#6B8E65',
  warmGray: '#8B8680',
  white: '#FFFFFF',
  lightGray: '#F8F9FA',
  mediumGray: '#6C757D',
} as const;

export const DEFAULT_THEME: Theme = {
  id: 'healthcare-default',
  name: 'Healthcare Default',
  colors: {
    background: HEALTHCARE_COLORS.offWhite,
    primary: HEALTHCARE_COLORS.deepSlate,
    secondary: HEALTHCARE_COLORS.mutedTeal,
    accent: HEALTHCARE_COLORS.forestGreen,
    text: HEALTHCARE_COLORS.softBlack,
    textSecondary: HEALTHCARE_COLORS.warmGray,
  },
  typography: {
    titleFont: 'Inter',
    bodyFont: 'Inter',
    titleSize: '4.5rem',
    bodySize: '1.25rem',
  },
  spacing: {
    padding: '4rem',
    margin: '2rem',
  },
};

export const STAKEHOLDER_PROFILES: StakeholderProfile[] = [
  {
    type: 'patient',
    name: 'Patient',
    description: 'Individuals managing their health journey with support from caregivers',
    primaryConcerns: [
      'Privacy and data security',
      'Ease of use and accessibility',
      'Clear communication with care team',
      'Maintaining independence',
      'Understanding their health status'
    ],
    keyMessages: [
      'Your privacy is paramount',
      'Simple, intuitive tools for better health',
      'Stay connected with your care team',
      'Maintain control over your health journey',
      'Clear, understandable health insights'
    ],
    tone: 'empathetic'
  },
  {
    type: 'caregiver',
    name: 'Caregiver',
    description: 'Family members and friends providing care and support',
    primaryConcerns: [
      'Coordinating care effectively',
      'Managing caregiver burnout',
      'Understanding patient needs',
      'Communicating with healthcare providers',
      'Ensuring patient safety'
    ],
    keyMessages: [
      'Streamlined care coordination tools',
      'Support for caregiver wellbeing',
      'Clear insights into patient status',
      'Seamless provider communication',
      'Peace of mind through transparency'
    ],
    tone: 'empathetic'
  },
  {
    type: 'clinician',
    name: 'Clinician',
    description: 'Healthcare providers delivering patient care',
    primaryConcerns: [
      'Clinical workflow efficiency',
      'Patient outcome improvement',
      'Data accuracy and completeness',
      'Regulatory compliance',
      'Integration with existing systems'
    ],
    keyMessages: [
      'Enhanced clinical workflows',
      'Better patient outcomes through data',
      'Comprehensive, accurate patient records',
      'Full regulatory compliance',
      'Seamless system integration'
    ],
    tone: 'clinical'
  },
  {
    type: 'mental-health',
    name: 'Mental Health / Chronic Care',
    description: 'Mental health providers and chronic care specialists',
    primaryConcerns: [
      'Long-term patient engagement',
      'Behavioral health tracking',
      'Crisis intervention capabilities',
      'Holistic care approach',
      'Outcome measurement'
    ],
    keyMessages: [
      'Sustained patient engagement',
      'Comprehensive behavioral insights',
      'Proactive crisis prevention',
      'Integrated mind-body care',
      'Measurable health improvements'
    ],
    tone: 'empathetic'
  },
  {
    type: 'enterprise',
    name: 'Enterprise Partnership',
    description: 'Healthcare organizations and enterprise partners',
    primaryConcerns: [
      'Scalability and reliability',
      'ROI and cost reduction',
      'Compliance and security',
      'Integration capabilities',
      'Market differentiation'
    ],
    keyMessages: [
      'Enterprise-grade scalability',
      'Proven ROI and cost savings',
      'Industry-leading security',
      'Flexible integration options',
      'Competitive market advantage'
    ],
    tone: 'professional'
  }
];

export const SLIDE_TEMPLATES = {
  statement: {
    name: 'Statement Slide',
    description: 'Full-screen impactful statement with supporting text',
    layout: 'centered'
  },
  'two-column': {
    name: 'Two Column',
    description: 'Side-by-side comparison or complementary content',
    layout: 'split'
  },
  'comparison-table': {
    name: 'Comparison Table',
    description: 'Structured comparison of features, benefits, or stakeholders',
    layout: 'tabular'
  },
  architecture: {
    name: 'Architecture Diagram',
    description: 'Visual system architecture or process flow',
    layout: 'diagram'
  },
  timeline: {
    name: 'Timeline',
    description: 'Chronological progression or roadmap',
    layout: 'horizontal'
  },
  quote: {
    name: 'Quote Slide',
    description: 'Testimonial or impactful quote with attribution',
    layout: 'centered'
  },
  'device-showcase': {
    name: 'Device Showcase',
    description: 'Product demonstration across devices',
    layout: 'showcase'
  },
  'closing-emotional': {
    name: 'Closing Emotional',
    description: 'Emotionally resonant closing statement',
    layout: 'centered'
  }
} as const;